import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  AlertTriangle,
  CreditCard,
  Mail,
  LogOut,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import ChangeOrgProfile from "../reusables/ChangeOrgProfile";
import axios from "axios";
import { fetchAndSetOrganization, recheckSubscriptionAndRedirectIfActive } from "../../utils/organizationService";

export default function SubscriptionEnded() {
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [portalLoader, setPortalLoader] = useState(false);
  const [recheckLoader, setRecheckLoader] = useState(false);

  // When landing on this page (e.g. return from Stripe), re-check subscription once
  useEffect(() => {
    recheckSubscriptionAndRedirectIfActive();
  }, []);

  const handleRecheckSubscription = async () => {
    setErrorMsg(null);
    setRecheckLoader(true);
    try {
      const redirected = await recheckSubscriptionAndRedirectIfActive();
      if (!redirected) {
        setErrorMsg("Subscription is not active yet. If you just renewed, try again in a moment.");
      }
    } finally {
      setRecheckLoader(false);
    }
  };

  const openManageSubscription = async () => {
    setErrorMsg(null);
    setPortalLoader(true);
    try {
      const org = JSON.parse(localStorage.getItem("organization"));
      const stripeCustId = org?.subscription_details?.M?.stripe_cust_id?.S;
      const orgId = org?.orgId?.S;
      if (!stripeCustId || !orgId) {
        setErrorMsg("Unable to open billing portal. Please contact support.");
        setPortalLoader(false);
        return;
      }
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/manage-billing",
        {
          customer_id: stripeCustId,
          organization_id: orgId,
        }
      );
      if (response?.data?.url) {
        window.location.href = response.data.url;
      } else {
        setErrorMsg("Unable to open billing portal. Please contact support.");
      }
    } catch (error) {
      // Log for production diagnostics when billing portal fails
      console.error(error);
      setErrorMsg(
        error.response?.data?.message ||
          "Unable to open billing portal. Please contact support."
      );
    } finally {
      setPortalLoader(false);
    }
  };

  const changeOrganization = async (org_id) => {
    try {
      await fetchAndSetOrganization(org_id);
      window.open("/dashboard", "_self");
    } catch (error) {
      // Log for production diagnostics when change organization fails
      console.error("SubscriptionEnded: change organization failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Card
        sx={{
          width: 420,
          p: 4,
          borderRadius: "14px",
          textAlign: "center",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            bgcolor: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AlertTriangle size={24} color="#DC2626" />
        </Box>

        {/* Title */}
        <Typography variant="h6" fontWeight={600} mb={0.5}>
          Subscription Ended
        </Typography>

        <Typography
          fontSize={14}
          color="text.secondary"
          mb={3}
        >
          Your subscription has ended. Renew to continue using Twilanswer.
        </Typography>

        {err_msg && (
          <Typography fontSize={13} color="error" sx={{ mb: 2 }}>
            {err_msg}
          </Typography>
        )}

        {/* Card message */}
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "10px",
            p: 2,
            mb: 3,
            bgcolor: "#FFFFFF",
          }}
        >
          <Typography fontSize={14} color="text.secondary">
            Renew your subscription to restore access to all features
            and continue managing your calls.
          </Typography>
        </Box>

        {/* Actions */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={
            portalLoader ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              <CreditCard size={16} />
            )
          }
          disabled={portalLoader}
          sx={{
            py: 1.2,
            borderRadius: "8px",
            mb: 1.5,
          }}
          onClick={openManageSubscription}
        >
          Manage Subscription
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={
            recheckLoader ? (
              <CircularProgress size={16} />
            ) : (
              <RefreshCw size={16} />
            )
          }
          disabled={recheckLoader}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            borderRadius: "8px",
            mb: 1.5,
          }}
          onClick={handleRecheckSubscription}
        >
          I've renewed, take me back
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Mail size={16} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            borderRadius: "8px",
            mb: 3,
          }}
          onClick={() => {
            window.location.href = "mailto:hello@twilanswer.com";
          }}
        >
          Contact Support
        </Button>

        {/* Features */}
        <Box textAlign="left" mb={3}>
          <Typography fontSize={13} fontWeight={600} mb={1}>
            What you’ll get with a paid plan:
          </Typography>

          {[
            "Call Routing and Automation",
            "Outbound Dialing",
            "Notifications Across Channels",
          ].map((item) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#22C55E",
                }}
              />
              <Typography fontSize={13} color="text.secondary">
                {item}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Footer */}

        <div className="px-3 w-full mb-5">
                              <ChangeOrgProfile changeOrganization={changeOrganization} />
                            </div>



        
          
          
          <Button
            startIcon={<LogOut size={16} />}
            sx={{
              textTransform: "none",
              color: "#6B7280",
              fontWeight: 500,
              border: "1px solid #D1D5DB",
              width:'100%'
            }}

            onClick={()=>{
                console.info("[Auth] Logging out: user chose Sign out from subscription-ended page.");
                localStorage.clear()
                window.location.href = '/login'
            }}
          >
            Sign out
          </Button>
      </Card>
    </Box>
  );
}
